import BaseCard from '../../../Prototype/Level/Card/BaseCard';
import TransformComponent from '../../../Component/Transform';
import CardSystem from '../CardSystem';
import ITask from '../../../Utility/Task/ITask';
import { delay } from '../../../Utility/Task/TaskQueue';
import {
    StartAnimation,
    PlayAnimation,
    GetAnimationClip,
} from '../../Animation/AnimationHelper';
import { AnimationClipEvent } from '../../Animation/AnimationClip';
import Game from '../../../Game';
import Renderable from '../../../Component/Renderable';
import GameObject from '../../../GameObject/GameObject';
import Point from '../../../Utility/Point';
import SpriteComponent from '../../../Component/Render/Sprite';
import StarBonusEffect from '../../../Prototype/Effect/StarBonusEffect';
import BonusTextEffect from '../../../Prototype/Effect/BonusTextEffect';
import { StartAudio } from '../../Audio/AudioHelper';
import { AudioTrackType } from '../../Audio/AudioClip';
import {
    AUDIO_WIN_SEQUENCE_POP,
    AUDIO_WIN_SEQUENCE_POP_01,
    AUDIO_WIN_SEQUENCE_POP_02,
    AUDIO_WIN_SEQUENCE_POP_03,
    AUDIO_WIN_SEQUENCE_POP_04,
    AUDIO_WIN_SEQUENCE_POP_05,
    AUDIO_WIN_SEQUENCE_POP_06,
    AUDIO_WIN_SEQUENCE_POP_07,
    AUDIO_WIN_SEQUENCE_POP_08,
    AUDIO_WIN_SEQUENCE_POP_09,
    AUDIO_WIN_SEQUENCE_POP_10,
    AUDIO_WIN_SEQUENCE_POP_11,
    AUDIO_WIN_SEQUENCE_POP_12,
    AUDIO_WIN_SEQUENCE_POP_13,
    AUDIO_WIN_SEQUENCE_POP_14,
    AUDIO_WIN_SEQUENCE_POP_15,
    AUDIO_WIN_SEQUENCE_POP_16,
    AUDIO_WIN_SEQUENCE_POP_17,
    AUDIO_WIN_SEQUENCE_POP_18,
    AUDIO_WIN_SEQUENCE_POP_19,
    AUDIO_WIN_SEQUENCE_POP_20,
    AUDIO_WIN_SEQUENCE_POP_21,
    AUDIO_WIN_SEQUENCE_POP_22,
    AUDIO_WIN_SEQUENCE_POP_23,
    AUDIO_WIN_SEQUENCE_POP_24,
    AUDIO_WIN_SEQUENCE_WOOSH,
} from '../../Audio';
import BonusCardsLayoutDataHelper, {
    IBonusCardLayout,
} from './BonusCardsLayoutDataHelper';
import Container from '../../../Prototype/Container';
import CardComponent from '../../../Component/Level/Card';
import { DragonbonesAnimationEvent } from '../../../Component/Dragonbones';
import { MathHelper } from '../../../Utility/MathHelper';
import { RenderSystemEvent } from '../../Render/RenderSystem';

export default class BonusCardsTask implements ITask<CardSystem> {
    public *execute(system: CardSystem): IterableIterator<number> {
        yield* delay(100);

        // Wait until all cards finish animating.
        const allCards = system.getAllCards();
        for (const card of allCards) {
            const cardComponent = card.getComponent(CardComponent);
            while (cardComponent.animating) yield;
        }

        const container = GameObject.Instantiate(Container);

        system.scene.addGameObject(container);

        const overlay = system.animator.find('overlay');

        container.addChild(overlay);

        const levelHUD = GameObject.findByName('rootElement.levelHUD');
        const suitcase = GameObject.findByName('rootElement.suitcaseBoosters');

        if (levelHUD && levelHUD.length > 0) {
            container.addChild(levelHUD[0]);
            container.addChild(suitcase[0]);
        }

        const cardsInStack = system.getCardsInStack();
        const cardsInPlay = system.getCardsInPlay();

        // Add cards in stack to container.
        for (const card of cardsInStack) {
            // Move to stack and reset position just in case.
            system.stackContainer.addChild(card);
            const transform = card.getComponent(TransformComponent);
            transform.position.set(-150, 0);

            // Add to animator.
            container.addChild(card);
        }

        // Add cards in play to container.
        for (const card of cardsInPlay) {
            // Remove modifiers.
            const modifiers = card.find('modifierContainer');
            if (modifiers) {
                for (const mod of modifiers.children) {
                    modifiers.removeChild(mod);
                }
            }

            // Add to animator.
            container.addChild(card);
        }

        // Add text above cards.
        container.addChild(system.cardStackText);

        system.scene.emit(RenderSystemEvent.BRING_TO_FRONT, overlay);
        overlay.getComponent(Renderable).visible = true;
        yield* PlayAnimation(GetAnimationClip('OverlayFadeIn', overlay));

        const bonusCardLayoutsData: IBonusCardLayout[][] = Game.resource.get(
            'bonus_card_layouts'
        );

        const cards = cardsInStack.concat(cardsInPlay);

        if (cards.length === 0) return;

        const layouts = BonusCardsLayoutDataHelper.getLayoutList(
            bonusCardLayoutsData,
            cards.length
        );

        // Process stack in reverse order.
        cards.reverse();

        let count = cards.length;

        // Get the slide and bonus animation delay (ms) based on the number of cards in stack.
        const slideDelay = MathHelper.clamp(3000 / count, 50, 150);
        const bonusDelay = MathHelper.clamp(2000 / count, 50, 100);

        const layoutContainer = GameObject.Instantiate(Container, {
            name: 'layoutContainer',
            pivot: new Point(0, 400),
            relativePosition: new Point(0.5, 0.5),
        });

        container.addChild(layoutContainer);

        // Flat array of cards collected for each card-bonus-explode loop.
        const layoutCardsList = [];

        for (let l = 0; l < layouts.length; l++) {
            const layoutData = layouts[l];
            const layoutCards = cards.splice(0, layoutData.length);

            layoutCardsList.push(layoutCards);

            // Move stack text to container above layoutContainer.
            container.addChild(system.cardStackText);

            // Re-parenting fix - must yield for next frame...
            yield;

            // Create a clone of the layoutCards array because it is not safe to
            // access an array while it is being sorted on Safari.
            const layoutCardsClone = layoutCards.slice(0);

            // Sort by deal-sequence.
            layoutCards.sort(
                (a, b) =>
                    layoutData[layoutCardsClone.indexOf(a)].dealSequence -
                    layoutData[layoutCardsClone.indexOf(b)].dealSequence
            );

            // Slide in each card.
            for (let i = 0; i < layoutCards.length; i++) {
                const card = layoutCards[i];

                // Add each card to layout container.
                layoutContainer.addChild(card);

                let nextCard: BaseCard;
                if (i + 1 < layoutCards.length) {
                    nextCard = layoutCards[i + 1]; // Next card in current layout.
                } else if (l + 1 < layouts.length) {
                    nextCard = cards[0]; // Top card in stack.
                }

                // Show next card if available.
                if (nextCard) {
                    nextCard.getComponent(Renderable).visible = true;
                }

                card.getComponent(Renderable).visible = true;

                card
                    .getChildByName('cardFrontSprite')
                    .getComponent(SpriteComponent).texture = 'cardBonus';

                StartAudio(
                    system.scene,
                    AUDIO_WIN_SEQUENCE_WOOSH,
                    AudioTrackType.SFX
                );

                StartAnimation(card, 'CardSlide', {
                    x: layoutData[i].x,
                    y: layoutData[i].y + l * 15,
                    rotation: (layoutData[i].angle / 180) * Math.PI + 0.0001,
                    duration: 250,
                }).on(AnimationClipEvent.COMPLETE, () => {
                    StartAnimation(card, 'CardFlip', { faceUp: true });
                });

                system.setCardStackText(--count);

                yield* delay(slideDelay);
            }

            // Sort by explode-sequence.
            layoutCards.sort(
                (a, b) =>
                    layoutData[layoutCardsClone.indexOf(a)].explodeSequence -
                    layoutData[layoutCardsClone.indexOf(b)].explodeSequence
            );
        }

        yield* delay(500);

        // Reverse the order of bonus cards "sections" exploding.
        layoutCardsList.reverse();

        system.scene.emit('UpdateScoreProgress', true);

        const audioPopClips = [
            AUDIO_WIN_SEQUENCE_POP_01,
            AUDIO_WIN_SEQUENCE_POP_02,
            AUDIO_WIN_SEQUENCE_POP_03,
            AUDIO_WIN_SEQUENCE_POP_04,
            AUDIO_WIN_SEQUENCE_POP_05,
            AUDIO_WIN_SEQUENCE_POP_06,
            AUDIO_WIN_SEQUENCE_POP_07,
            AUDIO_WIN_SEQUENCE_POP_08,
            AUDIO_WIN_SEQUENCE_POP_09,
            AUDIO_WIN_SEQUENCE_POP_10,
            AUDIO_WIN_SEQUENCE_POP_11,
            AUDIO_WIN_SEQUENCE_POP_12,
            AUDIO_WIN_SEQUENCE_POP_13,
            AUDIO_WIN_SEQUENCE_POP_14,
            AUDIO_WIN_SEQUENCE_POP_15,
            AUDIO_WIN_SEQUENCE_POP_16,
            AUDIO_WIN_SEQUENCE_POP_17,
            AUDIO_WIN_SEQUENCE_POP_18,
            AUDIO_WIN_SEQUENCE_POP_19,
            AUDIO_WIN_SEQUENCE_POP_20,
            AUDIO_WIN_SEQUENCE_POP_21,
            AUDIO_WIN_SEQUENCE_POP_22,
            AUDIO_WIN_SEQUENCE_POP_23,
            AUDIO_WIN_SEQUENCE_POP_24,
        ];
        let audioPopIndex = 0;

        let bonusValue = 5;
        for (const layoutCards of layoutCardsList) {
            for (const card of layoutCards) {
                const cardTransform = card.getComponent(TransformComponent);
                card.getComponent(Renderable).visible = false;
                const starBonus = GameObject.Instantiate(StarBonusEffect, {
                    position: cardTransform.position.clone(),
                });
                starBonus.on(DragonbonesAnimationEvent.COMPLETE, () =>
                    starBonus.destroy()
                );
                layoutContainer.addChild(starBonus);

                let audioClip = AUDIO_WIN_SEQUENCE_POP;
                if (audioPopIndex < audioPopClips.length) {
                    audioClip = audioPopClips[audioPopIndex++];
                }

                StartAudio(system.scene, audioClip, AudioTrackType.SFX);

                layoutContainer.addChild(
                    GameObject.Instantiate(BonusTextEffect, {
                        text: bonusValue.toString(),
                        anchor: new Point(0.5, 0.5),
                        position: cardTransform.position.clone(),
                    })
                );

                bonusValue += 5;

                yield* delay(bonusDelay);
            }
        }

        yield* delay(1000);
    }
}
